import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { ApiRequestError, downloadFile } from '../lib/api-client';
import { formatBytes, formatDateTime } from '../ui/format';
import { openShare, peekShare, type PublicShareView } from './api';

/**
 * The page somebody outside the tenant lands on (context.md §60–§65). Phase 138.
 *
 * It has no session, no navigation and nothing of the product around it: whoever opens this was
 * sent a link, and everything else on screen would be an invitation to wander into a CRM that is
 * not theirs.
 *
 * The download is a POST rather than an anchor, and that is not a detail — each one is counted
 * against the link's allowance, so it has to be a request this page makes rather than a URL the
 * browser can be pointed at twice.
 */

export function SharePage(): React.JSX.Element {
  const { token = '' } = useParams();
  const [password, setPassword] = useState('');
  const [content, setContent] = useState<PublicShareView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  const peek = useQuery({
    queryKey: ['share', token],
    queryFn: () => peekShare(token),
    retry: false,
  });

  const open = useMutation({
    mutationFn: () => openShare(token, password || null),
    onSuccess: (result) => {
      setError(null);
      setContent(result);
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  // A link with no password opens by itself: asking somebody to press "abrir" on a page that has
  // nothing to ask them is a step that exists only because it was easy to write.
  useEffect(() => {
    if (peek.data && !peek.data.needsPassword && !content && !open.isPending) open.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peek.data]);

  async function download(fileId: string, filename: string): Promise<void> {
    setDownloading(fileId);
    setError(null);

    try {
      // `downloadFile` already handles the blob, the naming and revoking the object URL — and the
      // revoking matters here, where somebody may take ten files in a row.
      await downloadFile(`/pub/shares/${encodeURIComponent(token)}/files/${fileId}`, {
        method: 'POST',
        body: password ? { password } : {},
        fallbackName: filename,
      });

      // What is left changes with every download, and the guest should see it change.
      if (content?.remainingDownloads !== null && content?.remainingDownloads !== undefined) {
        setContent({ ...content, remainingDownloads: Math.max(0, content.remainingDownloads - 1) });
      }
    } catch (caught) {
      setError(messageFor(caught));
    } finally {
      setDownloading(null);
    }
  }

  if (peek.isLoading) {
    return (
      <main className="share">
        <p className="muted">{t('state.loading')}</p>
      </main>
    );
  }

  /*
   * A link that is over says so, in the words of why.
   *
   * Expired, spent and revoked are three different things to the person who sent it, and telling
   * them apart is the difference between "ask for another" and "you were too late".
   */
  if (peek.isError) {
    const code = peek.error instanceof ApiRequestError ? peek.error.code : '';

    return (
      <main className="share">
        <h1>{t('share.title')}</h1>
        <p className="alert">
          {t(
            code === 'files.share_expired'
              ? 'share.expired'
              : code === 'files.share_revoked'
                ? 'share.revoked'
                : code === 'files.share_view_limit'
                  ? 'share.viewLimit'
                  : 'share.notFound',
          )}
        </p>
      </main>
    );
  }

  if (!content) {
    return (
      <main className="share">
        <h1>{peek.data?.name ?? t('share.title')}</h1>
        <p className="muted">{t('share.passwordNeeded')}</p>

        <form
          className="share__form"
          onSubmit={(event) => {
            event.preventDefault();
            if (password) open.mutate();
          }}
        >
          <input
            className="input"
            type="password"
            autoComplete="off"
            value={password}
            placeholder={t('share.password')}
            onChange={(event) => setPassword(event.target.value)}
          />

          <button type="submit" className="button button--primary" disabled={open.isPending}>
            {open.isPending ? t('state.loading') : t('share.open')}
          </button>
        </form>

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}
      </main>
    );
  }

  return (
    <main className="share">
      <h1>{content.name}</h1>

      <p className="muted small">
        {content.expiresAt
          ? t('share.expiresAt').replace('{when}', formatDateTime(content.expiresAt))
          : t('share.noExpiry')}
        {content.remainingDownloads !== null &&
          ` · ${t('share.downloadsLeft').replace('{n}', String(content.remainingDownloads))}`}
      </p>

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <ul className="share__files">
        {(content.files ?? []).map((file) => (
          <li key={file.id}>
            <span className="cell-stack">
              <span className="strong">{file.name}</span>
              <span className="muted small">{formatBytes(file.sizeBytes)}</span>
            </span>

            <button
              type="button"
              className="button button--primary button--compact"
              disabled={downloading !== null || content.remainingDownloads === 0}
              onClick={() => void download(file.id, file.name)}
            >
              {downloading === file.id ? t('state.loading') : t('share.download')}
            </button>
          </li>
        ))}
      </ul>

      {(content.files ?? []).length === 0 && <p className="muted">{t('share.empty')}</p>}
    </main>
  );
}
